import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useBionicReading } from '../../contexts/BionicReadingContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Switch } from '../ui/switch';
import { Salad, Sun, Moon, Contrast, Eye, User, LogOut, Settings, Menu, X } from 'lucide-react';
import { useState } from 'react';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme, highContrast, toggleHighContrast, isDark } = useTheme();
  const { bionicEnabled, toggleBionic } = useBionicReading();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const prideColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500'];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = isAuthenticated
    ? [
        { href: '/dashboard', label: 'Dashboard' },
        { href: '/diet-builder', label: 'Create Plan' },
        { href: '/grocery-list', label: 'Grocery List' },
      ]
    : [];

  return (
    <nav className="floating-nav flex items-center justify-between" role="navigation" aria-label="Main navigation">
      {/* Pride accent bar */}
      <div className="absolute -top-0.5 left-4 right-4 flex h-1 overflow-hidden rounded-t-full" aria-hidden="true">
        {prideColors.map((color, i) => (
          <div key={i} className={`flex-1 ${color}`} />
        ))}
      </div>
      
      <Link 
        to="/" 
        className="flex items-center gap-2 font-heading text-xl font-bold text-foreground"
        data-testid="nav-logo"
      >
        <Salad className="h-6 w-6 text-primary" aria-hidden="true" />
        <span className="hidden sm:inline">Gastro-Thrive</span>
      </Link>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-6">
        {navLinks.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className={`text-sm font-medium transition-colors hover:text-primary ${
              location.pathname === link.href ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center gap-3">
        {/* Accessibility Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              aria-label="Accessibility settings"
              data-testid="accessibility-menu-trigger"
            >
              <Settings className="h-5 w-5" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="accessibility-menu">
            <div className="px-3 py-2">
              <p className="text-sm font-medium">Accessibility</p>
            </div>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                <span className="text-sm">Dark Mode</span>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                aria-label="Toggle dark mode"
                data-testid="dark-mode-toggle"
              />
            </div>
            <div className="px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Contrast className="h-4 w-4" />
                <span className="text-sm">High Contrast</span>
              </div>
              <Switch
                checked={highContrast}
                onCheckedChange={toggleHighContrast}
                aria-label="Toggle high contrast mode"
                data-testid="high-contrast-toggle"
              />
            </div>
            <div className="px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span className="text-sm">Bionic Reading</span>
              </div>
              <Switch
                checked={bionicEnabled}
                onCheckedChange={toggleBionic}
                aria-label="Toggle bionic reading"
                data-testid="bionic-reading-toggle"
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Auth Actions */}
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full"
                aria-label="User menu"
                data-testid="user-menu-trigger"
              >
                <User className="h-5 w-5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="user-menu">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button 
            onClick={() => navigate('/auth')} 
            className="rounded-full"
            data-testid="sign-in-btn"
          >
            Sign In
          </Button>
        )}

        {/* Mobile Menu Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden rounded-full"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-2xl p-4 md:hidden shadow-lg"
          data-testid="mobile-menu"
        >
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="block py-3 px-4 text-sm font-medium hover:bg-muted rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};
